import { useState } from "react";
import type { Observation } from "../../models";
import type { SessionSource } from "../../sources/types";
import { ObservationsRail } from "./ObservationsRail";
import { ScenarioPanel } from "./ScenarioPanel";
import { ScorePanel } from "./ScorePanel";
import { TranscriptPane } from "./TranscriptPane";
import { type PlayerState, useSessionPlayer } from "./useSessionPlayer";

export interface SessionPlayerProps {
  sessionId: string;
  source: SessionSource;
}

// In replay mode the snapshot already contains every observation — show
// them all. In live mode they trickle in as turns arrive; show only those
// whose evidence falls within turns we've already received so the rail
// doesn't spoil the future.
function visibleObservations(state: PlayerState, mode: SessionSource["mode"]): Observation[] {
  if (mode === "replay") return state.observations;
  return state.observations.filter((o) => o.evidence.turn < state.turns.length);
}

export function SessionPlayer({ sessionId, source }: SessionPlayerProps) {
  const state = useSessionPlayer(sessionId, source);
  const [selectedObsId, setSelectedObsId] = useState<string | null>(null);

  if (state.status === "loading" || !state.header) {
    return <div className="player loading">Loading session…</div>;
  }
  if (state.status === "failed") {
    return <div className="player failed">Failed to load: {state.error}</div>;
  }

  return (
    <div className="player">
      <ScorePanel header={state.header} />
      <section className="transcript-pane">
        {source.mode === "live" ? (
          <div className="transport live">
            <span className="dot live" /> live · {state.turns.length} turns ingested
          </div>
        ) : null}
        {state.header.scenario ? (
          <ScenarioPanel scenario={state.header.scenario} userId={state.header.user} />
        ) : null}
        <TranscriptPane
          tenant={state.header.tenant}
          turns={state.turns}
          observations={state.observations}
          selectedObsId={selectedObsId}
        />
      </section>
      <ObservationsRail
        tenant={state.header.tenant}
        observations={visibleObservations(state, source.mode)}
        selectedObsId={selectedObsId}
        onSelectObs={setSelectedObsId}
      />
    </div>
  );
}
